import {
  AspectRatio,
  Box,
  Currency,
  IconLabel,
  Section,
  SectionProps,
  Stack,
  Text,
} from "@vinomofo/components";
import { BarrelIcon, LocationPinIcon, VarietalIcon } from "@vinomofo/icons";

import { AwardsBlock } from ".";
import { BlockWithHeading } from "../block-with-heading";
import { GatsbyImage } from "gatsby-plugin-image";
import { IconBadge } from "./IconBadge";
import type { Wine } from "../../types";
import { WineDescription } from "./WineDescription";
import { colorMap } from ".";
import { richTextParserRenderer } from "../../utils/rich-text-renderer";
import slugify from "slugify";

export interface WineNotesMixedProps extends Omit<SectionProps, "children"> {
  wine: Wine;
  isSpirit?: boolean;
}
export const WineNotesMixed = ({
  wine,
  isSpirit,
  ...rest
}: WineNotesMixedProps): JSX.Element => {
  const {
    awards,
    description,
    bottleRRP,
    bottleImage,
    name,
    region,
    tastingNotes,
    country,
    quantity,
    showRRPMixedCases,
    showTastingAndAwardsMixedCases,
  } = wine;
  const badgeWidth = "80px";
  const varietal =
    wine.category !== wine.varietal
      ? `${wine.category} — ${wine.varietal}`
      : wine.varietal;
  const location = country ? `${region} — ${wine.country}` : region;
  const title = quantity ? `${quantity}x ${name}` : name;

  return (
    <Section
      id={`notes-${slugify(name, { lower: true })}`}
      py={["25px", "40px"]}
      {...rest}
    >
      <Box display={["block", "block", "flex"]} justifyContent="space-between">
        <BlockWithHeading heading={title} divider={false}>
          <Stack space={1}>
            <IconLabel
              icon={isSpirit ? <BarrelIcon /> : <VarietalIcon />}
              iconSize="16px"
              color={colorMap[wine.category?.toLowerCase()] || "brand"}
              label={varietal}
            />
            <IconLabel
              icon={<LocationPinIcon />}
              iconSize="16px"
              label={location}
            />
          </Stack>
          <Stack space={3}>
            <WineDescription>
              {richTextParserRenderer(description)}
            </WineDescription>
            {bottleRRP && showRRPMixedCases && (
              <Text fontSize={["14px", "16px"]} fontWeight="bold">
                You’d pay <Currency amount={Math.ceil(bottleRRP)} /> full price.
              </Text>
            )}
          </Stack>
        </BlockWithHeading>

        <Box flex={1} py={["25px", "10px"]}>
          {bottleImage && (
            <AspectRatio ratio={[1, 16 / 9, 0.8]}>
              <GatsbyImage
                image={bottleImage}
                style={{ width: "100%", height: "100%", textAlign: "center" }}
                imgStyle={{ objectFit: "contain", bottom: 0 }}
                alt={name}
              />
            </AspectRatio>
          )}
        </Box>
        <Box flex={1}>
          {showTastingAndAwardsMixedCases && awards?.length > 0 && (
            <BlockWithHeading heading="Awards">
              <AwardsBlock awards={awards} badgeWidth={badgeWidth} />
            </BlockWithHeading>
          )}
          {showTastingAndAwardsMixedCases && tastingNotes.length && (
            <BlockWithHeading heading="Tasting Notes">
              <Box
                display="flex"
                flexWrap="wrap"
                justifyContent={["flex-start", "flex-start", "flex-start"]}
                gridGap={["0 20px", "0 98px", "0 40px"]}
              >
                {tastingNotes.map((tastingNote, i) => (
                  <IconBadge
                    width={badgeWidth}
                    marginBottom={["15px", "35px"]}
                    mx="5px"
                    key={i}
                    {...tastingNote}
                  />
                ))}
                {tastingNotes.length % 3 > 0 && (
                  <Box width={badgeWidth} mx="5px" />
                )}
              </Box>
            </BlockWithHeading>
          )}
        </Box>
      </Box>
    </Section>
  );
};
